import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateSubscriptionMutation } from "src/api/services/patientSubscriptionApi";
import { useSubscription } from "src/context/SubscriptionProvider";
import { useUpdateVisit } from "./useUpdateVisit";

export const useScheduleForCancellation = (subscriptionId: number) => {
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const updateVisit = useUpdateVisit();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const periodEnd = useSubscription((s) => s.current_period_end);
  const { t } = useTranslation();

  const handleUpdateSubscription = useCallback(async () => {
    if (subscriptionId == null) return null;

    try {
      setLoading(true);
      setOpenModal(true);
      const updatedSub = await updateSubscription({
        id: subscriptionId,
        scheduled_for_cancellation_at: periodEnd,
        cancellation_requested_at: new Date().toISOString(),
        status: "scheduled_for_cancellation",
      });

      let msg: string = "";

      if (updatedSub.error != null) {
        msg = t(
          "schedule-cancellation.error",
          "An error has occured when proccessing your cancellation, please contact our team.",
        );
        setError(msg);
      } else {
        msg = t(
          `schedule-cancellation.success`,
          "Your subscription has been scheduled for cancellation",
        );
        setMessage(msg);

        updateVisit({
          completed_at: new Date().toISOString(),
          status: "completed",
        });
      }
    } catch (err) {
      console.error(
        `Unable to schedule subscription ${subscriptionId} for cancellation`,
        err,
      );
    } finally {
      setLoading(false);
    }
  }, [periodEnd, subscriptionId, t, updateSubscription, updateVisit]);

  return {
    scheduleCancellation: handleUpdateSubscription,
    openModal,
    loading,
    message,
    error,
  };
};
